import { articleBodySchema, type Block } from '@auditwire/contracts';
import type { Tables } from '@/shared/types/tables';
import type { Article, ArticleAuthor, ArticleSummary } from './types';

type ProfileLite = Pick<
  Tables<'profiles'>,
  'id' | 'name' | 'initials' | 'avatar_color' | 'avatar_url'
>;
/** A to-one embed may arrive as an object or a single-element array depending on the query. */
type AuthorRel = ProfileLite | ProfileLite[] | null;

function normalizeAuthor(author: AuthorRel): ArticleAuthor | null {
  const p = Array.isArray(author) ? (author[0] ?? null) : author;
  if (!p) return null;
  return {
    id: p.id,
    name: p.name,
    initials: p.initials,
    avatarColor: p.avatar_color,
    avatarUrl: p.avatar_url,
  };
}

/** Validate & coerce the JSONB body into typed blocks (empty on malformed data). */
export function parseBody(body: unknown): Block[] {
  const result = articleBodySchema.safeParse(body);
  return result.success ? result.data : [];
}

type SummaryRow = Pick<
  Tables<'articles'>,
  'id' | 'slug' | 'title' | 'excerpt' | 'category' | 'cover_url' | 'read_time' | 'published_at'
> & { author: AuthorRel };

export function mapArticleSummary(row: SummaryRow): ArticleSummary {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt,
    category: row.category,
    coverUrl: row.cover_url,
    readTime: row.read_time,
    publishedAt: row.published_at,
    author: normalizeAuthor(row.author),
  };
}

type ArticleRow = Tables<'articles'> & { author: AuthorRel };

export function mapArticle(row: ArticleRow): Article {
  return {
    ...mapArticleSummary(row),
    status: row.status,
    body: parseBody(row.body),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
